import { forwardRef, useContext, useEffect, useMemo, useState } from 'react';

import { Link as RouterLink, useLocation } from 'react-router-dom';

import {
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Avatar,
  Badge,
  Collapse,
  Tooltip,
  Typography,
  useTheme,
  ListItemButton,
} from '@mui/material';

import useHelps from '../hooks/useHelps';

import {
  AttachFile,
  Dashboard,
  GridOn,
  PhonelinkSetup,
  DashboardOutlined,
  WhatsApp,
  SyncAlt,
  SettingsOutlined,
  PeopleAltOutlined,
  ContactPhoneOutlined,
  AccountTreeOutlined,
  FlashOn,
  HelpOutline,
  LocalOffer,
  ExpandLess,
  ExpandMore,
  Business,
} from '@mui/icons-material';

import { WhatsAppsContext } from '../context/WhatsApp/WhatsAppsContext';
import { AuthContext } from '../context/Auth/AuthContext';
import useActiveMenu from '../context/ActiveMenuContext/useActiveMenu';
import { Can } from '../components/Can';

import { i18n } from '../translate/i18n';
import moment from 'moment';
import { getStyled } from './styles';

const ConditionalTooltip = ({ children, tooltipEnabled, primary }) =>
  tooltipEnabled ? (
    <Tooltip title={primary} placement='right'>
      {children}
    </Tooltip>
  ) : (
    children
  );

function ListItemLink({ icon, primary, to, tooltip, showBadge }) {
  const theme = useTheme();
  const location = useLocation();
  const { activeMenu } = useActiveMenu();
  const isActive = activeMenu === to || location.pathname === to;
  const classes = getStyled(theme, isActive);

  const renderLink = useMemo(
    () =>
      forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to],
  );

  return (
    <ConditionalTooltip tooltipEnabled={!!tooltip} primary={primary}>
      <li>
        <ListItemButton component={renderLink} sx={classes.listItem}>
          {icon ? (
            <ListItemIcon>
              {showBadge ? (
                <Badge
                  badgeContent='!'
                  color='error'
                  overlap='circular'
                  sx={classes.badge}
                >
                  <Avatar sx={classes.iconHoverActive}>{icon}</Avatar>
                </Badge>
              ) : (
                <Avatar sx={classes.iconHoverActive}>{icon}</Avatar>
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={
              <Typography sx={classes.listItemText}>{primary}</Typography>
            }
          />
        </ListItemButton>
      </li>
    </ConditionalTooltip>
  );
}

const MainListItems = ({ collapsed, drawerClose }) => {
  const theme = useTheme();
  const [managementHover, setManagementHover] = useState(false);
  const location = useLocation();
  const isManagementActive =
    location.pathname === '/' || location.pathname.startsWith('/dashboard');
  location.pathname.startsWith('/moments');
  const classes = getStyled(theme, isManagementActive, managementHover);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openDashboardSubmenu, setOpenDashboardSubmenu] = useState(false);
  const [planExpired, setPlanExpired] = useState(false);

  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);

  useEffect(() => {
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, [list]);

  useEffect(() => {
    if (location.pathname.startsWith('/tickets')) {
      setActiveMenu('/tickets');
    } else {
      setActiveMenu('');
    }
  }, [location, setActiveMenu]);

  useEffect(() => {
    async function fetchData() {
      setPlanExpired(moment(moment().format()).isBefore(user.company.dueDate));
    }
    fetchData();
  }, [user]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === 'qrcode' ||
            whats.status === 'PAIRING' ||
            whats.status === 'DISCONNECTED' ||
            whats.status === 'TIMEOUT' ||
            whats.status === 'OPENING'
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const [dashboardSubmenuOpen, setDashboardSubmenuOpen] = useState(false);

  const handleDashboardSubmenuToggle = () => {
    setDashboardSubmenuOpen(!dashboardSubmenuOpen);
    setOpenDashboardSubmenu(!openDashboardSubmenu);
  };

  return (
    <div
      onClick={drawerClose}
      style={{
        marginLeft: collapsed && '-15%',
        transition: 'margin-left 0.2s ease-in-out',
        color: theme.custom.colors.text,
      }}
    >
      {planExpired && (
        <Can
          role={
            (user.profile === 'user' && user.showDashboard === 'enabled') ||
            user.allowRealTime === 'enabled'
              ? 'admin'
              : user.profile
          }
          perform={'drawer-admin-items:view'}
          yes={() => (
            <>
              <Tooltip
                title={
                  collapsed ? i18n.t('mainDrawer.listItems.management') : ''
                }
                placement='right'
              >
                <ListItemButton
                  dense
                  onClick={handleDashboardSubmenuToggle}
                  onMouseEnter={() => setManagementHover(true)}
                  onMouseLeave={() => setManagementHover(false)}
                >
                  <ListItemIcon>
                    <Avatar sx={classes.iconDashboardHoverActive}>
                      <Dashboard />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={classes.listItemText}>
                        {i18n.t('mainDrawer.listItems.management')}
                      </Typography>
                    }
                  />
                  {openDashboardSubmenu ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </Tooltip>
              <Collapse
                in={openDashboardSubmenu}
                timeout='auto'
                unmountOnExit
                sx={{
                  backgroundColor: theme.custom.colors.defaulBackground,
                }}
              >
                <Can
                  role={
                    user.profile === 'user' && user.showDashboard === 'enabled'
                      ? 'admin'
                      : user.profile
                  }
                  perform={'drawer-admin-items:view'}
                  yes={() => (
                    <>
                      <ListItemLink
                        small
                        to='/'
                        primary='Dashboard'
                        icon={<DashboardOutlined />}
                        tooltip={collapsed}
                      />
                    </>
                  )}
                />
                <Can
                  role={
                    user.profile === 'user' && user.allowRealTime === 'enabled'
                      ? 'admin'
                      : user.profile
                  }
                  perform={'drawer-admin-items:view'}
                  yes={() => (
                    <ListItemLink
                      to='/moments'
                      primary={i18n.t('mainDrawer.listItems.chatsTempoReal')}
                      icon={<GridOn />}
                      tooltip={collapsed}
                    />
                  )}
                />
              </Collapse>
            </>
          )}
        />
      )}
      {planExpired && (
        <ListItemLink
          to='/tickets'
          primary={i18n.t('mainDrawer.listItems.tickets')}
          icon={<WhatsApp />}
          tooltip={collapsed}
        />
      )}

      {planExpired && (
        <ListItemLink
          to='/quick-messages'
          primary={i18n.t('mainDrawer.listItems.quickMessages')}
          icon={<FlashOn />}
          tooltip={collapsed}
        />
      )}

      {planExpired && (
        <ListItemLink
          to='/contacts'
          primary={i18n.t('mainDrawer.listItems.contacts')}
          icon={<ContactPhoneOutlined />}
          tooltip={collapsed}
        />
      )}

      {planExpired && (
        <ListItemLink
          to='/tags'
          primary={i18n.t('mainDrawer.listItems.tags')}
          icon={<LocalOffer />}
          tooltip={collapsed}
        />
      )}

      {hasHelps && planExpired && (
        <ListItemLink
          to='/helps'
          primary={i18n.t('mainDrawer.listItems.helps')}
          icon={<HelpOutline />}
          tooltip={collapsed}
        />
      )}
      <Can
        role={
          user.profile === 'user' && user.allowConnections === 'enabled'
            ? 'admin'
            : user.profile
        }
        perform='dashboard:view'
        yes={() => (
          <>
            <Divider
              sx={{
                borderBottomColor: theme.custom.colors.text,
                marginTop: 1,
                marginBottom: 1,
              }}
            />
            {!collapsed && (
              <ListSubheader inset sx={classes.text}>
                {i18n.t('mainDrawer.listItems.administration')}
              </ListSubheader>
            )}

            {planExpired && (
              <Can
                role={user.profile}
                perform='dashboard:view'
                yes={() => (
                  <ListItemLink
                    to='/users'
                    primary={i18n.t('mainDrawer.listItems.users')}
                    icon={<PeopleAltOutlined />}
                    tooltip={collapsed}
                  />
                )}
              />
            )}

            {planExpired && (
              <Can
                role={user.profile}
                perform='dashboard:view'
                yes={() => (
                  <ListItemLink
                    to='/queues'
                    primary={i18n.t('mainDrawer.listItems.queues')}
                    icon={<AccountTreeOutlined />}
                    tooltip={collapsed}
                  />
                )}
              />
            )}

            {planExpired && (
              <Can
                role={
                  user.profile === 'user' && user.allowConnections === 'enabled'
                    ? 'admin'
                    : user.profile
                }
                perform={'drawer-admin-items:view'}
                yes={() => (
                  <ListItemLink
                    to='/connections'
                    primary={i18n.t('mainDrawer.listItems.connections')}
                    icon={<SyncAlt />}
                    showBadge={connectionWarning}
                    tooltip={collapsed}
                  />
                )}
              />
            )}

            {user.super && (
              <ListItemLink
                to='/allConnections'
                primary={i18n.t('mainDrawer.listItems.allConnections')}
                icon={<PhonelinkSetup />}
                tooltip={collapsed}
              />
            )}

            {planExpired && (
              <Can
                role={user.profile}
                perform='dashboard:view'
                yes={() => (
                  <ListItemLink
                    to='/files'
                    primary={i18n.t('mainDrawer.listItems.files')}
                    icon={<AttachFile />}
                    tooltip={collapsed}
                  />
                )}
              />
            )}

            {planExpired && (
              <Can
                role={user.profile}
                perform='dashboard:view'
                yes={() => (
                  <ListItemLink
                    to='/settings'
                    primary={i18n.t('mainDrawer.listItems.settings')}
                    icon={<SettingsOutlined />}
                    tooltip={collapsed}
                  />
                )}
              />
            )}

            {user.super && (
              <ListItemLink
                to='/companies'
                primary={i18n.t('mainDrawer.listItems.companies')}
                icon={<Business />}
                tooltip={collapsed}
              />
            )}
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
